import React, { useState } from 'react';
import { Trophy, Medal, Award, TrendingUp, Target, Coins, Calendar, Clock, User, ChevronLeft, ChevronRight } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useAuth } from '../contexts/AuthContext';

const LeaderboardPage = () => {
  const { isLoggedIn } = useAuth();
  const [activeTab, setActiveTab] = useState('daily'); // daily, weekly, monthly
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // 模拟排行榜数据
  const generateLeaderboardData = () => {
    const cryptos = ['BTC', 'ETH', 'SOL', 'ADA', 'DOT', 'MATIC', 'AVAX', 'LINK', 'UNI', 'DOGE'];
    const names = [
      'CryptoMaster', 'PredictionKing', 'BlockchainPro', 'TokenHunter', 'CoinWhisperer',
      'TradingGuru', 'CryptoSage', 'DigitalOracle', 'ChainAnalyst', 'TokenTrader',
      'CryptoProphet', 'BlockExpert', 'CoinMaster', 'TradingWiz', 'CryptoNinja',
      'TokenGuru', 'ChainMaster', 'CryptoPro', 'BlockTrader', 'CoinExpert'
    ];
    
    const users = Array.from({ length: 100 }, (_, i) => ({
      id: i + 1,
      username: `${names[i % names.length]}${Math.floor(Math.random() * 100)}`, // 缩短用户名
      totalCoins: Math.floor(Math.random() * 50000) + 1000,
      joinDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000),
      mainCrypto: cryptos[Math.floor(Math.random() * cryptos.length)],
      winRate: (Math.random() * 40 + 60).toFixed(1), // 60-100%
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${i}`
    }));
    
    // 按 PLYP Coins 排序后重新分配排名
    return users
      .sort((a, b) => b.totalCoins - a.totalCoins)
      .map((user, index) => ({
        ...user,
        rank: index + 1 // 重新分配排名 1-100
      }));
  };

  const leaderboardData = generateLeaderboardData();
  
  // 模拟当前用户数据
  const currentUser = {
    username: 'YourUsername',
    rank: Math.floor(Math.random() * 100) + 1,
    totalCoins: Math.floor(Math.random() * 30000) + 5000,
    winRate: (Math.random() * 25 + 70).toFixed(1), // 70-95%
    mainCrypto: 'BTC'
  };
  
  // 获取前三名数据
  const topThree = leaderboardData.slice(0, 3);
  
  // 分页数据
  const totalPages = Math.ceil(leaderboardData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = leaderboardData.slice(startIndex, startIndex + itemsPerPage);

  // 格式化日期
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // 格式化数字
  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  // 获取排名颜色
  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-400';
    if (rank === 2) return 'text-gray-300';
    if (rank === 3) return 'text-orange-400';
    return 'text-gray-400';
  };

  // 获取排名图标
  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Award className="w-6 h-6 text-orange-400" />;
    return <span className="text-lg font-bold text-gray-400">#{rank}</span>;
  };

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="text-2xl font-bold text-white">Prediction Leaderboard</h1>
                <p className="text-gray-400">
                  Top performers in crypto predictions
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {/* 我的排名 - 仅在登录时显示 */}
        {isLoggedIn && (
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-400/30 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">My Ranking</h3>
                <p className="text-blue-300 text-sm">Your current position</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-10">
                <div className="text-right">
                  <p className="text-gray-400 text-xs mb-1">Rank</p>
                  <div className="flex items-center space-x-2">
                    {currentUser.rank <= 3 ? (
                      currentUser.rank === 1 ? <Trophy className="w-5 h-5 text-yellow-400" /> :
                      currentUser.rank === 2 ? <Medal className="w-5 h-5 text-gray-400" /> :
                      <Award className="w-5 h-5 text-orange-400" />
                    ) : null}
                    <span className="text-white font-bold text-xl">#{currentUser.rank}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs mb-1">PLYP Coins</p>
                  <div className="flex items-center space-x-2">
                    <Coins className="w-4 h-4 text-yellow-400" />
                    <span className="text-white font-bold text-lg">{formatNumber(currentUser.totalCoins)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs mb-1">Win Rate</p>
                  <span className="text-green-400 font-bold text-lg">{currentUser.winRate}%</span>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs mb-1">Main Crypto</p>
                  <span className="bg-orange-500/20 text-orange-300 px-2 py-1 rounded text-sm font-medium">
                    {currentUser.mainCrypto}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* 时间段选择 */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'daily'
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <Clock className="w-4 h-4" />
            <span>Daily</span>
          </button>
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'weekly'
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>Weekly</span>
          </button>
          <button
            onClick={() => setActiveTab('monthly')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'monthly'
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            <span>Monthly</span>
          </button>
        </div>

        {/* 前三名领奖台 */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-white mb-6 text-center">
            🏆 Top 3 Champions
          </h2>
          <div className="flex items-end justify-center space-x-8">
            {/* 第二名 */}
            <div className="flex flex-col items-center">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 w-48 mb-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Medal className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1 truncate px-2">{topThree[1]?.username}</h3>
                  <p className="text-gray-400 text-sm mb-2">#{topThree[1]?.rank}</p>
                  <div className="bg-gray-500/20 rounded-lg p-3">
                    <p className="text-gray-300 text-xs mb-1">PLYP Coins</p>
                    <p className="text-white font-bold text-lg">{formatNumber(topThree[1]?.totalCoins)}</p>
                  </div>
                </div>
              </div>
              <div className="w-48 h-20 bg-gradient-to-t from-gray-600 to-gray-500 rounded-t-lg flex items-center justify-center">
                <span className="text-white font-bold text-2xl">2</span>
              </div>
            </div>

            {/* 第一名 */}
            <div className="flex flex-col items-center">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 w-52 mb-4">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-1 truncate px-2">{topThree[0]?.username}</h3>
                  <p className="text-yellow-400 text-sm mb-2">👑 Champion</p>
                  <div className="bg-yellow-500/20 rounded-lg p-3">
                    <p className="text-yellow-300 text-xs mb-1">PLYP Coins</p>
                    <p className="text-white font-bold text-xl">{formatNumber(topThree[0]?.totalCoins)}</p>
                  </div>
                </div>
              </div>
              <div className="w-52 h-28 bg-gradient-to-t from-yellow-600 to-yellow-500 rounded-t-lg flex items-center justify-center">
                <span className="text-white font-bold text-3xl">1</span>
              </div>
            </div>

            {/* 第三名 */}
            <div className="flex flex-col items-center">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 w-48 mb-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1 truncate px-2">{topThree[2]?.username}</h3>
                  <p className="text-gray-400 text-sm mb-2">#{topThree[2]?.rank}</p>
                  <div className="bg-orange-500/20 rounded-lg p-3">
                    <p className="text-orange-300 text-xs mb-1">PLYP Coins</p>
                    <p className="text-white font-bold text-lg">{formatNumber(topThree[2]?.totalCoins)}</p>
                  </div>
                </div>
              </div>
              <div className="w-48 h-16 bg-gradient-to-t from-orange-600 to-orange-500 rounded-t-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">3</span>
              </div>
            </div>
          </div>
        </div>

        {/* 排行榜列表 */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
          {/* 表头 */}
          <div className="bg-white/10 px-6 py-4 border-b border-white/10">
            <div className="grid gap-4 text-sm font-medium text-gray-300" style={{gridTemplateColumns: '80px 2fr 1.2fr 1fr 1fr 1.2fr 1fr'}}>
              <div>Rank</div>
              <div>User</div>
              <div>PLYP Coins</div>
              <div>Join Date</div>
              <div>Main Crypto</div>
              <div>Win Rate</div>
              <div>Actions</div>
            </div>
          </div>

          {/* 列表内容 */}
          <div className="divide-y divide-white/10">
            {currentData.map((user, index) => (
              <div key={user.id} className="px-6 py-4 hover:bg-white/5 transition-colors">
                <div className="grid gap-4 items-center" style={{gridTemplateColumns: '80px 2fr 1.2fr 1fr 1fr 1.2fr 1fr'}}>
                  {/* 排名 */}
                  <div className="flex items-center space-x-2">
                    {getRankIcon(user.rank)}
                  </div>

                  {/* 用户信息 */}
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate" title={user.username}>{user.username}</p>
                      <p className="text-gray-400 text-xs">ID: {user.id.toString().padStart(4, '0')}</p>
                    </div>
                  </div>

                  {/* PLYP Coins */}
                  <div className="flex items-center space-x-2">
                    <Coins className="w-4 h-4 text-yellow-400" />
                    <span className="text-white font-semibold">{formatNumber(user.totalCoins)}</span>
                  </div>

                  {/* 入驻时间 */}
                  <div className="text-gray-400 text-sm">
                    {formatDate(user.joinDate)}
                  </div>

                  {/* 主要预测币种 */}
                  <div>
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full font-medium">
                      {user.mainCrypto}
                    </span>
                  </div>

                  {/* 胜率 */}
                  <div className="flex items-center space-x-2">
                    <div className="w-12 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                        style={{ width: `${user.winRate}%` }}
                      ></div>
                    </div>
                    <span className="text-green-400 text-sm font-medium">{user.winRate}%</span>
                  </div>

                  {/* 操作 */}
                  <div>
                    <button className="text-blue-400 hover:text-blue-300 text-sm font-medium">
                      View Profile
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 分页 */}
        <div className="flex items-center justify-between mt-6">
          <div className="text-gray-400 text-sm">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, leaderboardData.length)} of {leaderboardData.length} users
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center space-x-1 px-3 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center space-x-1 px-3 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
